/**
 * NOTE: For compatibility across platforms all calls **must** return a
 * `Promise` object!
 */

import * as Platform from '../Platform';
import { PlayerAbstract } from '../PlayerAbstract';
import { IframeMessengerMixin } from '../mixins/IframeMessengerMixin';
import { BrowserReloadable } from '../mixins/BrowserReloadable';
import { Log } from '../../AuxillaryService/Log';
import { Deferred } from '../../Utility/Deferred';
import _ from 'underscore';
import { isUrlQueryPlatform } from '../PlatformCheck';

/**
 * Iframe messenger request
 * @typedef {object} IframeMessengerRequest
 * @memberof ChromeExtension.
 * @property {string} action
 * @property {number} id - unique identifier for request promise
 * @property {Object.<string, string|number>} options - other options
 */

/**
 * Iframe messenger response
 * @typedef {object} IframeMessengerResponse
 * @memberof ChromeExtension.
 * @property {string} origin - Iframe Origin
 * @property {object} source - Has information about request source
 * @property {Object.<string, string|number>} data - Response data
 */

/**
 * Chrome Player
 * @constructor
 * @augments PlayerAbstract
 * @implements NamedValueCacheInterface
 * @implements ApplicationStateInterface
 * @implements RestartApplicationInterface
 * @implements ScreenshotInterface
 * @implements ReloadInterface
 * @implements LogInterface
 * @implements SystemInformationInterface
 * @mixes IframeMessengerMixin
 * @mixes BrowserReloadable
 * @deprecated Will still need to be maintained while the existing clients still use this.
 */
function ChromeExtensionPlayer() {
    PlayerAbstract.call(this);

    //To initialize Porthole because ChromeExtensionPlayer runs signs in an
    //iframe
    IframeMessengerMixin.call(this, '/proxy.html');
    this.addIframeEventListener(parentIframeMessageHandler.bind(this));

    /**
     * Array of request promises
     * @member {Array} ChromeExtensionPlayer#promises
     */
    Object.defineProperty(this, 'promises', {
        value: [],
        writable: false,
        configurable: false,
        enumerable: false,
    });
}

ChromeExtensionPlayer.prototype = Object.create(BrowserReloadable(PlayerAbstract).prototype);
ChromeExtensionPlayer.prototype.constructor = ChromeExtensionPlayer;

/**
 * @inheritDoc
 */
ChromeExtensionPlayer.prototype.type = Platform.CHROME_EXTENSION;

/**
 * @inheritDoc
 */
ChromeExtensionPlayer.prototype.name = 'ChromeExtension';

/**
 * @inheritDoc
 */
ChromeExtensionPlayer.prototype.getHostPlatform = function () {
    return Promise.resolve(this.name);
};

/**
 * Gets network status
 * @returns {Deferred}
 */
ChromeExtensionPlayer.prototype.getNetworkStatus = function () {
    return invokePlayerMethod.call(this, 'GET_NETWORK_STATUS');
};

/**
 * @typedef {Object} DeviceInformation
 * @memberof ChromeExtension.
 * @property {string} vendor
 * @property {string} browserPlatform
 * @property {string} browserName
 * @property {string} browserVersion
 * @property {string} model
 * @property {string} platform
 * @property {string} osName
 * @property {string} osVersion
 * @property {string} uniqueIdentifier - This value is reset on extension
 * uninstall
 */

/**
 * Gets Device information
 * @async
 * @returns {Deferred<ChromeExtension.DeviceInformation>}
 */
ChromeExtensionPlayer.prototype.getDeviceInformation = function () {
    return invokePlayerMethod.call(this, 'GET_DEVICE_INFORMATION');
};

/**
 * Returns a device-specific hardware ID based on ChromeOS info.
 *
 * @return {Promise<string>}
 */
ChromeExtensionPlayer.prototype._generateAndGetHardwareId = function () {
    return this.getDeviceInformation().then(function (deviceInfo) {
        return (
            'Chrome://' +
            deviceInfo.platform +
            ':' +
            deviceInfo.model +
            ':' +
            deviceInfo.uniqueIdentifier
        );
    });
};

/**
 * @inheritDoc
 */
ChromeExtensionPlayer.prototype.getNamedValue = function (keyName, defaultValue) {
    return invokePlayerMethod.call(this, 'GET_NAMED_VALUE', {
        key: keyName,
        value: defaultValue,
    });
};

/**
 * @inheritDoc
 */
ChromeExtensionPlayer.prototype.setNamedValue = function (keyName, keyValue) {
    return invokePlayerMethod.call(this, 'SET_NAMED_VALUE', {
        key: keyName,
        value: keyValue,
    });
};

/**
 * @inheritDoc
 */
ChromeExtensionPlayer.prototype.deleteNamedValue = function (keyName) {
    return invokePlayerMethod.call(this, 'DELETE_NAMED_VALUE', {
        key: keyName,
    });
};

/**
 * @inheritDoc
 */
ChromeExtensionPlayer.prototype.KeyExists = function (keyName) {
    return invokePlayerMethod.call(this, 'CONFIG_KEY_EXISTS', {
        key: keyName,
    });
};

/**
 * @inheritDoc
 */
ChromeExtensionPlayer.prototype.getPlayerVersion = function () {
    return invokePlayerMethod.call(this, 'GET_PLAYER_VERSION');
};

/**
 * @inheritDoc
 */
ChromeExtensionPlayer.prototype.getScreenshot = function () {
    return invokePlayerMethod.call(this, 'GET_SCREENSHOT');
};

/**
 * @inheritDoc
 */
ChromeExtensionPlayer.prototype.getPlatformLogs = function () {
    return getData.call(this, 'PLATFORM_LOGS');
};

/**
 * Set log level on the player
 * @param {string} logLevel - info, warn, error, debug
 * @returns {Deferred}
 */
ChromeExtensionPlayer.prototype.setLogLevel = function (logLevel) {
    var setLogAction = {
        info: 'SET_LOG_LEVEL_INFO',
        warn: 'SET_LOG_LEVEL_WARN',
        error: 'SET_LOG_LEVEL_ERROR',
        debug: 'SET_LOG_LEVEL_DEBUG',
    };
    if (!setLogAction[logLevel]) {
        return Deferred.rejected(new Error('Invalid log level.'));
    } else {
        return performPlayerAction.call(this, setLogAction[logLevel]);
    }
};

/**
 * Enables file logging on player
 * @returns {Deferred}
 */
ChromeExtensionPlayer.prototype.enablePersistentLog = function () {
    return performPlayerAction.call(this, 'ENABLE_PERSISTENT_LOG');
};

/**
 * Disables file logging in player
 * @returns {Deferred}
 */
ChromeExtensionPlayer.prototype.disablePersistentLog = function () {
    return performPlayerAction.call(this, 'DISABLE_PERSISTENT_LOG');
};

/**
 * @typedef ApplicationState
 * @memberof ChromeExtension.
 * @property {string} appState
 * @property {string} playerUuid
 * @property {string} deviceId
 * @property {boolean} isOnline
 * @property {string} logLevel
 * @property {boolean} persistLogs
 * @property {string} orientation
 * @property {boolean} startOnBoot
 * @property {boolean} startOnIdle
 * @property {boolean} wakeLockAcquired
 * @property {boolean} sleepOnNoContent
 * @property {object[]} streamSchedules
 * @property {number} volume
 * @property {boolean} muted
 * @property {string} build
 * @property {ChromeExtension.DeviceInformation} device
 */

/**
 * Gets current application state
 * @returns {Deferred<ChromeExtension.ApplicationState>}
 */
ChromeExtensionPlayer.prototype.getApplicationState = function () {
    return getData.call(this, 'APPLICATION_STATE');
};

/**
 * Gets System information - Hardware and OS
 * Pascal case for compatibility
 * @returns {Promise}
 */
ChromeExtensionPlayer.prototype.getSystemInformation = function () {
    return getData.call(this, 'SYSTEM_INFORMATION').then(function (data) {
        return {
            HardwareInfo: {
                Manufacturer: data.hardware.manufacturer,
                Model: data.hardware.model,
            },
            OperatingSystemInfo: {
                Name: data.os.name,
                Manufacturer: data.os.manufacturer,
                Version: data.os.version,
            },
        };
    });
};

/**
 * Does a cold restart of the application
 * @returns {Promise}
 */
ChromeExtensionPlayer.prototype.restartApplication = function () {
    this.postMessageToParentIframe({
        action: 'APPLICATION_RESTART',
    });

    return Promise.resolve();
};

/**
 * Set no scheduled content state in ChromeOS device
 * @returns {Deferred}
 */
ChromeExtensionPlayer.prototype.setNoScheduledContent = function () {
    return performPlayerAction.call(this, 'NO_SCHEDULED_CONTENT');
};

/**
 * Checks if the device is running ChromeOS or Chrome Extension
 * @returns {boolean}
 */
ChromeExtensionPlayer.isTheRunningPlatform = function () {
    const thisPlatformIsRunning = isUrlQueryPlatform('ChromeExtension');
    if (typeof thisPlatformIsRunning === 'boolean') {
        return thisPlatformIsRunning;
    }

    // use fallback check
    // This is injected when iframe is created by the ChromeExtension
    var platform = document.documentElement.getAttribute('platform');
    return platform === 'ChromeExtension';
};

/** Aliases for compatibility **/

/**
 * @inheritDoc
 */
ChromeExtensionPlayer.prototype.GetValueOrDefault = ChromeExtensionPlayer.prototype.getNamedValue;

/**
 * @inheritDoc
 */
ChromeExtensionPlayer.prototype.GetValue = ChromeExtensionPlayer.prototype.getNamedValue;

/**
 * @inheritDoc
 */
ChromeExtensionPlayer.prototype.SetValue = ChromeExtensionPlayer.prototype.setNamedValue;

/**
 * @inheritDoc
 */
ChromeExtensionPlayer.prototype.DeleteValue = ChromeExtensionPlayer.prototype.deleteNamedValue;

/**
 * Stores last used message id
 * @type {number}
 */
ChromeExtensionPlayer.messageIdCounter = 0;

/**
 * Store boot time to prevent collision with message id in the platform layer
 * Collision is possible if the signs layer refreshes but
 * the platform does not refresh and persists the same message id list
 * @type {number}
 */
ChromeExtensionPlayer.bootTime = Date.now().toString().padStart(14, '0');

/**
 * Generates unique id that is used to identify request/response messages,
 * so that multiple requests can be sent
 * @returns {number}
 * @private
 */
function getMessageId() {
    return parseInt(
        (++ChromeExtensionPlayer.messageIdCounter).toString() + ChromeExtensionPlayer.bootTime
    );
}

/**
 * Handler for any message coming from parent Iframe
 * @param {ChromeExtensionPlayer.IframeMessengerResponse} event - Response event
 * @returns {void}
 * @throws Error
 * @private
 */
function parentIframeMessageHandler(event) {
    var message = event.data;

    /**
     * If data property is not present the message is invalid or
     * the origin is not valid
     */
    if (!message) {
        return;
    }
    var promiseIndex = _.findLastIndex(this.promises, { id: message.id });
    if (promiseIndex === -1) {
        throw new Error('Cannot resolve. Promise not found for ' + message.action);
    }

    Log.Debug('Message received from parent frame: ', null, __fileline, message);

    if (!message.action) {
        throw new Error("'action' should be present in the message.");
    }

    switch (message.action) {
        case 'GET_SCREENSHOT':
            this.promises[promiseIndex].promise.resolve({
                //remove the data type part because CMS adds it
                base64Image: message.data.replace('data:image/jpeg;base64,', ''),
            });
            break;
        case 'GET_PLAYER_VERSION':
        case 'GET_NAMED_VALUE':
        case 'SET_NAMED_VALUE':
        case 'DELETE_NAMED_VALUE':
        case 'CONFIG_KEY_EXISTS':
        case 'GET_NETWORK_STATUS':
        case 'GET_DEVICE_INFORMATION':
        case 'GET_DATA':
        case 'PLAYER_ACTION':
        case 'GET_LOGS':
            this.promises[promiseIndex].promise.resolve(message.data);
            break;
        default:
            throw Error("'action' not recognized.");
    }

    this.promises.splice(promiseIndex, 1);
}

/**
 * Gets data from player
 * @param {string} type - type of data
 * @returns {Deferred}
 * @private
 */
function getData(type) {
    return invokePlayerMethod.call(this, 'GET_DATA', { type: type });
}

/**
 * Performs a player action
 * @param {string} type
 * @returns {Deferred}
 * @private
 */
function performPlayerAction(type) {
    return invokePlayerMethod.call(this, 'PLAYER_ACTION', { type: type });
}

/**
 * Creates request promise that sends message to platform (Chrome Extension)
 * layer and pushes it to promises array. The promise is resolve once the
 * platform layer sends a message back
 * @see More information on how the message is processed
 * {@link https://bitbucket.org/reachmedianetwork/signs-unified/src/master/src/services/signs/SignsIframeMessengerService.ts}
 * @param {string} action
 * @param {Object.<string, string|number>} options - miscellaneous options for
 * request
 * @returns {Deferred}
 * @private
 */
function invokePlayerMethod(action, options) {
    var getDataPromise = new Deferred();
    var id = getMessageId.call(this);
    this.postMessageToParentIframe(
        Object.assign(
            {
                action: action,
                id: id,
            },
            options
        )
    );

    this.promises.push({
        id: id,
        promise: getDataPromise,
    });

    return getDataPromise;
}

export { ChromeExtensionPlayer };
